import { Switch } from '@headlessui/react'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'
import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'

import { useAivyContext, useLanguageContext } from '../../../context'
import { getCriterionDimensions } from '../../../helpers/career-adjustment'
import { useSetState } from '../../../hooks/use-set-state'
import ChallengeInfoModal from '../../challenge-info-modal'
import SlideOverDimensionDescription from '../../team-analyse/SlideOverDimensionDescription'

const I18N_PREFIX = 'create_career.must_criteria_selection.'

/**
 * Controlled. Formik, Mutation und der eigene Speichern-Button sind entfallen,
 * der Draft liegt in Adjustment. Der Fallback fuer mandatory_criteria (Stelle
 * ohne vorheriges confirm-testbattery) liegt jetzt in readAdjustmentDraft.
 */
const MustCriteriaSelection = ({
  dimensions,
  mandatoryCriteria,
  onToggleCriterion
}) => {
  const { t } = useTranslation()
  const { system } = useAivyContext()
  const { language } = useLanguageContext()

  // use FLANKER or other challenges with visible for the modal transition
  const [infoModalChallenge, setInfoModalChallenge] = useSetState({
    ...system.challenges.FLANKER,
    visible: false
  })

  const [openDimensionDescription, setOpenDimensionsDescription] =
    useState(false)

  const criterionDimensions = getCriterionDimensions({
    dimensions,
    systemDimensions: system.dimensions_pdf_reports
  })

  return (
    <>
      <h2 className='text-2xl/7 font-bold text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight'>
        {t(I18N_PREFIX + 'title')}
      </h2>
      <ReactMarkdown className='mt-2 max-w-2xl text-sm/6 text-gray-900'>
        {t(I18N_PREFIX + 'description')}
      </ReactMarkdown>
      <div className='mt-6'>
        {criterionDimensions.map((d) => {
          const dimensionFromSystem = system.dimensions_pdf_reports[d]

          const challenges = dimensionFromSystem.measured_by.map(
            (exam_id) => system.challenges[exam_id]
          )

          return (
            <div
              key={d}
              className='mb-2 flex flex-row items-center justify-between rounded-lg border border-slate-100 bg-slate-50 p-4'
            >
              <div>
                <span
                  onClick={() => {
                    setOpenDimensionsDescription(dimensionFromSystem)
                  }}
                  className='flex cursor-pointer items-center font-semibold hover:opacity-70'
                >
                  {dimensionFromSystem.property[language]}
                  <InformationCircleIcon className='ml-1 inline-block h-6 w-6 text-gray-900' />
                </span>
                <div className='mt-1' />
                <span className='text-sm font-normal'>
                  {t(I18N_PREFIX + 'measured_by')}
                  {challenges.map((challenge, index) => (
                    <Fragment key={index}>
                      <span
                        className='cursor-pointer text-sm font-semibold text-blue-600 underline'
                        onClick={() => {
                          setInfoModalChallenge({
                            ...challenge,
                            visible: true
                          })
                        }}
                      >
                        {challenge.title[language]}
                      </span>
                      <span className='last:hidden'>{', '}</span>
                    </Fragment>
                  ))}
                </span>
              </div>
              <Switch
                // Ohne aria-label hat der Schalter keinen Accessible Name, die
                // Dimension daneben ist nur visuell zugeordnet.
                aria-label={t(I18N_PREFIX + 'switch_label', {
                  dimension: dimensionFromSystem.property[language]
                })}
                checked={mandatoryCriteria.includes(d)}
                onChange={() => onToggleCriterion(d)}
                className='group relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 data-[checked]:bg-blue-600'
              >
                <span
                  aria-hidden='true'
                  className='pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5'
                />
              </Switch>
            </div>
          )
        })}
      </div>
      <SlideOverDimensionDescription
        open={openDimensionDescription}
        setOpen={setOpenDimensionsDescription}
      />
      <ChallengeInfoModal
        open={infoModalChallenge.visible}
        setOpen={() => setInfoModalChallenge({ visible: false })}
        challenge={infoModalChallenge}
      />
    </>
  )
}

MustCriteriaSelection.propTypes = {
  dimensions: PropTypes.arrayOf(PropTypes.string).isRequired,
  mandatoryCriteria: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggleCriterion: PropTypes.func.isRequired
}

export default MustCriteriaSelection
